import type { AiDecisionFeedItem } from "@/api/types";
interface AiFeedProps {
  decisions: AiDecisionFeedItem[];
}
const DECISION_STYLE: Record<
  AiDecisionFeedItem["decision"],
  {
    label: string;
    text: string;
    dot: string;
  }
> = {
  ENTER: { label: "Enter", text: "text-gold", dot: "bg-gold" },
  SKIP: { label: "Skip", text: "text-ink-dim", dot: "bg-ink-faint" },
  CLOSE: { label: "Close", text: "text-turquoise", dot: "bg-turquoise" },
};
function timeLabel(timestampMs: number): string {
  const seconds = Math.max(0, Math.floor((Date.now() - timestampMs) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}
export default function AiFeed({ decisions }: AiFeedProps) {
  if (decisions.length === 0) {
    return (
      <div className="rounded-xl border border-line bg-panel p-6 text-center font-mono text-xs text-ink-faint">
        No AI decisions logged yet. The feed fills in after the first scan cycle.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {decisions.map((item) => {
        const style = DECISION_STYLE[item.decision];
        return (
          <div key={item.id} className="rounded-lg border border-line bg-panel p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
                <span className={`font-mono text-xs font-semibold uppercase tracking-wide ${style.text}`}>
                  {style.label}
                </span>
                <span className="font-mono text-sm text-ink">{item.symbol}</span>
                {item.direction && (
                  <span className="font-mono text-[11px] uppercase text-ink-faint">{item.direction}</span>
                )}
                {!item.riskApproved && item.decision === "ENTER" && (
                  <span className="rounded-full bg-sell/10 px-1.5 py-0.5 font-mono text-[10px] text-sell">blocked</span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {item.confidence !== null && (
                  <span className="font-mono text-[11px] tabular text-ink-dim">{item.confidence}%</span>
                )}
                <span className="font-mono text-[10px] text-ink-faint">{timeLabel(item.createdAt)}</span>
              </div>
            </div>
            <p className="mt-1.5 text-xs leading-relaxed text-ink-dim">{item.reasoning}</p>
            {!item.riskApproved && item.riskReason && (
              <p className="mt-1 font-mono text-[11px] text-sell">Risk layer: {item.riskReason}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
